import { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, Navigate } from 'react-router-dom';
import {
  HomeIcon,
  UsersIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';

export default function AdminLayout() {
  const navigate = useNavigate();
  const [isAuthed, setIsAuthed] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Check for Google auth token
    const authData = localStorage.getItem('adminAuth');
    if (authData) {
      try {
        const { expiresAt } = JSON.parse(authData);
        if (Date.now() < expiresAt) {
          setIsAuthed(true);
          setIsInitialized(true);
          return;
        }
      } catch (error) {
        console.error('Auth data parse error:', error);
      }
    }
    
    setIsInitialized(true);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  if (!isInitialized) return null;
  if (!isAuthed) return <Navigate to="/admin/login" replace />;

  const navItems = [
    { to: '/admin', icon: HomeIcon, label: 'Dashboard', end: true },
    { to: '/admin/users', icon: UsersIcon, label: 'Users' },
    { to: '/admin/conversations', icon: ChatBubbleLeftRightIcon, label: 'Conversations' },
    { to: '/admin/prompts', icon: DocumentTextIcon, label: 'Prompts' },
    { to: '/admin/metrics', icon: ChartBarIcon, label: 'Metrics' },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Nav */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img src="/simplytechwise-logo.png" alt="Simply TechWise" className="h-10" />
              <span className="text-xl font-semibold text-gray-800">
                WiseAssist <span className="text-wise">Admin</span>
                <sub className="text-xs text-gray-500 ml-1">v{import.meta.env.VITE_APP_VERSION || '0.1.0'}</sub>
              </span>
            </div>

            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span className="hidden sm:block">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Navigation Tabs */}
        <nav className="flex gap-1 mb-6 bg-white rounded-lg p-1 shadow-sm border border-gray-200 overflow-x-auto">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? 'bg-wise text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Content */}
        <Outlet />
      </div>
    </div>
  );
}
